// FM Operations - Shared Utility Functions
// KPI status calculation based on Excel formulas from Operations Bible

/**
 * Calculate gym status from KPI values.
 * GREEN = All compliance >= 100% AND Renewal >= 80%
 * YELLOW = Any compliance 90-99% OR renewal 70-79%
 * RED = Any compliance < 90% OR renewal < 70%
 */
export function calculateGymStatus(kpis) {
  const {
    compliance_7day_pct = 0,
    compliance_14day_pct = 0,
    compliance_30day_pct = 0,
    renewal_pct = 0
  } = kpis || {};

  const complianceValues = [compliance_7day_pct, compliance_14day_pct, compliance_30day_pct];
  const minCompliance = Math.min(...complianceValues);

  // RED: Any compliance < 90% OR renewal < 70%
  if (minCompliance < 90 || renewal_pct < 70) return 'RED';

  // YELLOW: Any compliance 90-99% OR renewal 70-79%
  if (minCompliance < 100 || renewal_pct < 80) return 'YELLOW';

  // GREEN: All compliance >= 100% AND renewal >= 80%
  return 'GREEN';
}

/**
 * Get Tailwind CSS class strings for a status color.
 */
export function getStatusColors(status) {
  const colors = {
    RED: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200', indicator: 'bg-red-500', bgLight: 'bg-red-50' },
    YELLOW: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200', indicator: 'bg-yellow-500', bgLight: 'bg-yellow-50' },
    GREEN: { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-200', indicator: 'bg-green-500', bgLight: 'bg-green-50' },
  };
  return colors[status] || colors.RED;
}

/**
 * Format number as Indian currency: "Rs X,XX,XXX"
 */
export function formatCurrency(value) {
  if (value == null || isNaN(value)) return 'Rs 0';
  const num = Math.round(Number(value));
  // Indian number system: last 3 digits, then groups of 2
  const str = num.toString();
  if (str.length <= 3) return `Rs ${str}`;
  const last3 = str.slice(-3);
  const rest = str.slice(0, -3);
  const formatted = rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
  return `Rs ${formatted}`;
}

/**
 * Get current IST time as Date object.
 */
export function getISTTime() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
}

/**
 * Get current time slot based on IST hour.
 * morning_peak: 6-10 AM, off_peak: 10 AM-4 PM, evening_peak: 4-10 PM, closed: 10 PM-6 AM
 */
export function getTimeSlot() {
  const istTime = getISTTime();
  const hour = istTime.getHours();

  if (hour >= 6 && hour < 10) return 'morning_peak';
  if (hour >= 10 && hour < 16) return 'off_peak';
  if (hour >= 16 && hour < 22) return 'evening_peak';
  return 'closed';
}

/**
 * Get human-readable label for a time slot.
 */
export function getTimeSlotLabel(slot) {
  const labels = {
    morning_peak: 'Morning Peak (6-10 AM)',
    off_peak: 'Off-Peak (10 AM-4 PM)',
    evening_peak: 'Evening Peak (4-10 PM)',
    closed: 'Closed (10 PM-6 AM)',
  };
  return labels[slot] || slot;
}

/**
 * Get time-adaptive greeting based on IST hour.
 */
export function getGreeting() {
  const hour = getISTTime().getHours();
  if (hour >= 6 && hour < 12) return 'Good morning';
  if (hour >= 12 && hour < 17) return 'Good afternoon';
  return 'Good evening';
}
